import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import PageHeader from "../../components/common/PageHeader";
import { useToast, ToastContainer } from "../../components/common/Toast";
import {
  createAdminPromoCode,
  type CreatePromoCodeData,
} from "../../services/paymentsService";

const CreatePromoCode: React.FC = () => {
  const navigate = useNavigate();
  const {
    toasts,
    removeToast,
    success: showSuccess,
    error: showError,
  } = useToast();

  const [code, setCode] = useState("");
  const [description, setDescription] = useState("");
  const [planTier, setPlanTier] = useState("pro");
  const [durationMonths, setDurationMonths] = useState(6);
  const [maxRedemptions, setMaxRedemptions] = useState("");
  const [validUntil, setValidUntil] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!code.trim()) {
      showError("Promo code is required");
      return;
    }

    setLoading(true);

    try {
      const promoData: CreatePromoCodeData = {
        code: code.trim().toUpperCase(),
        plan_tier: planTier,
        duration_months: durationMonths,
      };

      if (description.trim()) {
        promoData.description = description.trim();
      }

      if (maxRedemptions.trim()) {
        promoData.max_redemptions = parseInt(maxRedemptions, 10);
      }

      if (validUntil) {
        promoData.valid_until = validUntil;
      }

      const result = await createAdminPromoCode(promoData);

      if (result.success) {
        showSuccess("Promo code created successfully!");
        setTimeout(() => {
          navigate("/admin");
        }, 2000);
      } else {
        showError(result.error || "Failed to create promo code");
      }
    } catch (err) {
      showError(
        err instanceof Error ? err.message : "Failed to create promo code"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <PageHeader
        title="Create Promo Code"
        subtitle="Create a promotional code that grants free access to a plan"
      />

      <div className="bg-gradient-to-br from-gray-900 to-gray-800 border border-gray-700 rounded-lg p-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Code Input */}
          <div>
            <label
              htmlFor="promoCode"
              className="block text-sm font-medium text-gray-300 mb-2"
            >
              Promo Code <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              id="promoCode"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="e.g., LAUNCH6MO"
              disabled={loading}
              className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed font-mono tracking-wider"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label
              htmlFor="description"
              className="block text-sm font-medium text-gray-300 mb-2"
            >
              Description
            </label>
            <input
              type="text"
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g., Launch promo for early adopters"
              disabled={loading}
              className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>

          {/* Plan Tier & Duration Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label
                htmlFor="planTier"
                className="block text-sm font-medium text-gray-300 mb-2"
              >
                Plan Tier
              </label>
              <select
                id="planTier"
                value={planTier}
                onChange={(e) => setPlanTier(e.target.value)}
                disabled={loading}
                className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="pro">Pro</option>
                <option value="basic">Basic</option>
              </select>
            </div>

            <div>
              <label
                htmlFor="durationMonths"
                className="block text-sm font-medium text-gray-300 mb-2"
              >
                Duration (months)
              </label>
              <input
                type="number"
                id="durationMonths"
                value={durationMonths}
                onChange={(e) => setDurationMonths(parseInt(e.target.value, 10) || 1)}
                min={1}
                max={24}
                disabled={loading}
                className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>
          </div>

          {/* Max Redemptions & Valid Until Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label
                htmlFor="maxRedemptions"
                className="block text-sm font-medium text-gray-300 mb-2"
              >
                Max Redemptions
              </label>
              <input
                type="number"
                id="maxRedemptions"
                value={maxRedemptions}
                onChange={(e) => setMaxRedemptions(e.target.value)}
                min={1}
                placeholder="Unlimited"
                disabled={loading}
                className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <p className="mt-1 text-xs text-gray-500">
                Leave empty for unlimited redemptions
              </p>
            </div>

            <div>
              <label
                htmlFor="validUntil"
                className="block text-sm font-medium text-gray-300 mb-2"
              >
                Valid Until
              </label>
              <input
                type="date"
                id="validUntil"
                value={validUntil}
                onChange={(e) => setValidUntil(e.target.value)}
                disabled={loading}
                className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <p className="mt-1 text-xs text-gray-500">
                Leave empty for no expiry
              </p>
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-blue-900/20 border border-blue-800/50 rounded-lg p-4">
            <div className="flex items-start">
              <svg
                className="w-5 h-5 text-blue-400 mr-3 flex-shrink-0 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div className="text-sm text-blue-300">
                <p className="font-semibold mb-1">How promo codes work</p>
                <ul className="list-disc list-inside space-y-1 text-blue-200">
                  <li>
                    Users can apply promo codes during onboarding or via a signup
                    link with <code className="text-blue-300">?promo=CODE</code>
                  </li>
                  <li>
                    Redeemed codes grant the selected plan tier for the specified
                    duration, bypassing Stripe
                  </li>
                  <li>
                    After expiry, the user reverts to the Free plan unless they
                    subscribe
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-700">
            <button
              type="button"
              onClick={() => navigate("/admin")}
              disabled={loading}
              className="px-6 py-2 text-gray-300 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !code.trim()}
              className="px-8 py-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-purple-800 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {loading ? (
                <span className="flex items-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Creating...
                </span>
              ) : (
                "Create Promo Code"
              )}
            </button>
          </div>
        </form>
      </div>

      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  );
};

export default CreatePromoCode;
